import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const CATEGORIES = [
  { key: "all", label: "ALL DROPS" },
  { key: "tees", label: "TEES" },
  { key: "hoodies", label: "HOODIES" },
  { key: "caps", label: "CAPS" },
  { key: "accessories", label: "GEAR" },
];

const CAT_LABELS = { tees: "Graphic Tees", hoodies: "Hoodies", caps: "Caps", accessories: "Accessories" };

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  // Full catalog (unfiltered) — used to compute drop numbers so filtering doesn't renumber
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Full catalog once — for drop numbering (oldest = 001)
    api.get(`/products`).then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = cat !== "all" ? `?category=${cat}` : "";
    api.get(`/products${q}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [cat]);

  // Map product_id -> drop number based on created_at ASC
  const dropMap = useMemo(() => {
    const sorted = [...allProducts].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const m = {};
    sorted.forEach((p, i) => { m[p.id] = i + 1; });
    return m;
  }, [allProducts]);

  const catLabel = CAT_LABELS[cat] || "Full Collection";
  const canonical = cat === "all" ? "https://toxicstreet.co/shop" : `https://toxicstreet.co/shop?category=${cat}`;

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <Seo
        title={`${catLabel} · Shop`}
        description={`Shop the full TOXIC STREET ${catLabel.toLowerCase()} collection. ${products.length}+ limited-drop pieces. Free US shipping over $100. Stay dangerous.`}
        canonical={canonical}
      />
      <div className="border-b border-white/20 pb-8 mb-10">
        <p className="text-blood text-xs tracking-[0.3em] font-mono mb-2">// CATALOG</p>
        <h1 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter">
          THE FULL <span className="text-blood">ARSENAL</span>
        </h1>
        <p className="text-ash text-sm mt-4 max-w-2xl">
          EVERY PIECE BUILT FOR THE STREETS. LIMITED RUNS. ONCE THEY'RE GONE, THEY'RE GONE.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-10">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`category-filter-${c.key}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (c.key === "all") next.delete("category");
              else next.set("category", c.key);
              setParams(next);
            }}
            className={`px-5 py-2 text-xs tracking-[0.2em] uppercase font-mono border transition-colors ${
              cat === c.key
                ? "bg-blood text-bone border-blood"
                : "border-white/20 text-bone hover:bg-bone hover:text-ink"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div data-testid="shop-loading" className="font-mono text-ash text-center py-20">
          <span className="text-blood animate-pulse">+ + +</span> LOADING DROP <span className="text-blood animate-pulse">+ + +</span>
        </div>
      ) : products.length === 0 ? (
        <div data-testid="shop-empty" className="font-marker text-blood text-3xl text-center py-20">
          nothing here, yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} drop={dropMap[p.id]} />
          ))}
        </div>
      )}
    </div>
  );
}
